import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import ChampSelect from '../ui/ChampSelect';
import { justifierAnomalie, corrigerHeures } from './apiJustification';

/**
 * ============================================================================
 * JUSTIFIER UNE ANOMALIE DE POINTAGE
 * ============================================================================
 *
 * Ouverte depuis le bouton « Justifier » d'une ligne de Ressources humaines >
 * Présence/Temps > Par lot. Jusqu'au 14/10/2026, aucun écran ne permettait de
 * justifier une anomalie du personnel, et « Envoyer à la compta » restait
 * grisé pour toujours.
 *
 * Deux gestes (décision de Daniel du 14/10/2026) :
 *  - Justifier : l'écart est reconnu et expliqué, les heures ne bougent pas.
 *  - Corriger l'heure : l'heure badgée est fausse (départ oublié, double
 *    passage, borne allumée en retard). Le serveur recalcule retard et durée
 *    avec les règles de la borne ; l'heure d'origine reste dans les observations.
 *
 * Les heures saisies sont celles de l'école. La paie ne lit pas le statut RH :
 * justifier n'annule aucune retenue, cela débloque l'envoi en comptabilité.
 *
 * Fenêtre et non page : c'est une action, pas une consultation (CLAUDE.md,
 * « pas de modals pour afficher les détails »). Format formulaire à deux
 * colonnes de DESIGN-SYSTEM.md.
 */

const MOTIFS = [
  { valeur: 'retard_autorise', libelle: 'Retard autorisé' },
  { valeur: 'mission', libelle: 'Mission ou déplacement de service' },
  { valeur: 'probleme_borne', libelle: 'Problème de borne' },
  { valeur: 'oubli_badge', libelle: 'Oubli du badge' },
  { valeur: 'autre', libelle: 'Autre (à préciser)' }
];

const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';
const hhmm = (valeur) => {
  const correspondance = String(valeur || '').match(/(\d{2}):(\d{2})/);
  return correspondance ? `${correspondance[1]}:${correspondance[2]}` : '';
};

const Ligne = ({ libelle, valeur }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-gray-500">{libelle}</span>
    <span className="font-medium text-gray-900">{valeur || '-'}</span>
  </div>
);

// `seulementCorriger` : la fenêtre n'offre que « Corriger l'heure » - journée
// en cours (la justification attend la fin de la journée), départ oublié sans
// anomalie, ou ligne déjà justifiée ou corrigée à reprendre (revue du 14/10/2026).
const JustifierAnomalieDialog = ({ ouvert, onFermer, employe, pointage, anomalies = [], dateLibelle, onEnregistre, seulementCorriger = false }) => {
  const [geste, setGeste] = useState('justifier');
  const [motif, setMotif] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [arrivee, setArrivee] = useState('');
  const [depart, setDepart] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Chaque ouverture repart des heures de la ligne.
  useEffect(() => {
    if (!ouvert) return;
    setGeste(seulementCorriger ? 'corriger' : 'justifier');
    setMotif('');
    setCommentaire('');
    setArrivee(hhmm(pointage?.heure_arrivee || pointage?.heure));
    setDepart(hhmm(pointage?.heure_depart));
    setErreur(null);
  }, [ouvert, pointage, seulementCorriger]);

  const valider = async () => {
    setErreur(null);
    if (!motif) { setErreur('Choisissez un motif.'); return; }
    if (motif === 'autre' && !commentaire.trim()) { setErreur('Précisez le motif dans le commentaire.'); return; }
    if (geste === 'corriger' && !arrivee) { setErreur("L'heure d'arrivée est obligatoire."); return; }
    if (geste === 'corriger' && depart && depart <= arrivee) { setErreur("L'heure de départ doit suivre l'heure d'arrivée."); return; }
    setEnCours(true);
    try {
      if (geste === 'corriger') {
        await corrigerHeures(pointage.id, { arrivee, depart: depart || null, motif, commentaire });
      } else {
        await justifierAnomalie(pointage.id, { motif, commentaire });
      }
      onEnregistre?.(geste);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  if (!pointage) return null;
  const prevu = `${hhmm(pointage.heure_debut_prevue) || '--:--'} - ${hhmm(pointage.heure_fin_prevue) || '--:--'}`;
  const badge = `${hhmm(pointage.heure_arrivee || pointage.heure) || '--:--'} - ${hhmm(pointage.heure_depart) || '--:--'}`;

  return (
    <Dialog open={ouvert} onOpenChange={(etat) => { if (!etat && !enCours) onFermer(); }}>
      <DialogContent className="w-[95vw] max-w-4xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-[15px] font-bold text-gray-900">{seulementCorriger ? "Corriger l'heure" : "Justifier l'anomalie"}</DialogTitle>
              <DialogDescription className="text-xs text-gray-500">
                {employe ? `${employe.prenom || ''} ${employe.nom || ''}`.trim() : ''}{dateLibelle ? ` - ${dateLibelle}` : ''}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Ce qui s'est passé */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Ce qui a été enregistré</p>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-gray-50/60 px-4 py-2">
                <Ligne libelle="Horaire prévu" valeur={prevu} />
                <Ligne libelle="Heures badgées" valeur={badge} />
                {pointage.justification_rh && <Ligne libelle="Justification déjà posée" valeur={pointage.justification_rh} />}
              </div>
              {anomalies.length > 0 && (
                <div className="mt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Anomalies</p>
                  <div className="flex flex-wrap gap-1.5">
                    {anomalies.map((libelle) => (
                      <span key={libelle} className="rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs text-orange-800">{libelle}</span>
                    ))}
                  </div>
                </div>
              )}
              <p className="mt-4 text-xs leading-relaxed text-gray-500">
                La paie ne tient pas encore compte de la justification : elle débloque l&apos;envoi en
                comptabilité et garde la trace de qui a justifié, quand et pourquoi.
              </p>
            </div>

            {/* Ce qu'on en fait */}
            <div className="space-y-4">
              {!seulementCorriger && <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Que faire ?</p>
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1">
                  {[
                    { valeur: 'justifier', libelle: 'Justifier', aide: 'Les heures restent' },
                    { valeur: 'corriger', libelle: "Corriger l'heure", aide: "L'heure badgée est fausse" }
                  ].map((option) => (
                    <button
                      key={option.valeur}
                      type="button"
                      onClick={() => setGeste(option.valeur)}
                      className={`rounded-md px-3 py-2 text-left transition-colors ${geste === option.valeur ? 'bg-white shadow-sm' : 'hover:bg-white/60'}`}
                    >
                      <span className={`block text-sm font-semibold ${geste === option.valeur ? 'text-[#246990]' : 'text-gray-700'}`}>{option.libelle}</span>
                      <span className="block text-[11px] text-gray-500">{option.aide}</span>
                    </button>
                  ))}
                </div>
              </div>}

              {geste === 'corriger' && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
                    Arrivée réelle
                    <input type="time" value={arrivee} onChange={(e) => setArrivee(e.target.value)} className="h-9 rounded-lg border border-gray-200 px-3 text-sm font-normal focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15" />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
                    Départ réel (facultatif)
                    <input type="time" value={depart} onChange={(e) => setDepart(e.target.value)} className="h-9 rounded-lg border border-gray-200 px-3 text-sm font-normal focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15" />
                  </label>
                </div>
              )}
              {/* Depuis le 03/11/2026, une correction d'heures se valide au
                  Centre de Support, comme toute saisie à la main. */}
              {geste === 'corriger' && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  La correction part au Centre de Support : la ligne reste « en validation », et ne part pas en
                  comptabilité, tant qu'elle n'est pas validée.
                </p>
              )}

              <div className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
                Motif
                <ChampSelect valeur={motif || undefined} onChange={setMotif} options={MOTIFS} placeholder="Choisir un motif…" className="font-normal" />
              </div>

              <label className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
                Commentaire {motif === 'autre' ? '(obligatoire)' : '(facultatif)'}
                <textarea
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Ce que l'agent a expliqué, la pièce fournie…"
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-normal focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
                />
              </label>

              {erreur && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{erreur}</p>}
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 py-4 border-t bg-gray-50">
          <Button variant="outline" className={BOUTON_PIED} onClick={onFermer} disabled={enCours}>Annuler</Button>
          <Button className={`${BOUTON_PIED} bg-[#246990] hover:bg-[#1f5a7a] text-white`} onClick={valider} disabled={enCours}>
            {enCours && <Loader2 className="h-4 w-4 animate-spin" />}
            {geste === 'corriger' ? 'Envoyer la correction' : 'Justifier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JustifierAnomalieDialog;
